<template>
    <v-card class="elevation-1 round-card background-1 mr-4">
        <!-- Header sempre visibile - cliccabile per toggle -->
        <v-card-title
            class="pa-3 pb-1 cursor-pointer"
            @click="$emit('toggle')"
        >
            <div class="d-flex align-center flex-wrap" style="width: 100%">
                <!-- Avatar utente -->
                <template v-if="comment.user">
                    <v-avatar
                        v-if="comment.user.avatar"
                        size="32"
                        class="mr-2 custom-avatar"
                        :style="{'background-image': 'url(' + comment.user.avatar + ')'}"
                    ></v-avatar>
                    <v-avatar
                        v-else
                        :class="$eventBusUtility.bgColor(comment.user.id || comment.user_id)"
                        size="32"
                        class="mr-2"
                    >
                        <span class="white--text fs14">{{ getInitial }}</span>
                    </v-avatar>
                </template>
                <v-avatar
                    v-else
                    :color="isActivity ? 'blue-grey' : 'orange'"
                    size="32"
                    class="mr-2"
                >
                    <v-icon dark small>{{ isActivity ? 'mdi-history' : 'mdi-note-text' }}</v-icon>
                </v-avatar>

                <div class="flex-grow-1">
                    <div class="subtitle-1 font-weight-medium" style="line-height: 1">
                        {{ comment.user ? comment.user.name : 'Sistema' }}
                    </div>
                    <div class="caption grey--text">
                        {{ isActivity ? 'Attività' : 'Nota privata' }}
                    </div>
                </div>
                <div class="caption grey--text text-right d-flex align-center">
                    <v-chip x-small class="mr-2" :color="isActivity ? 'blue-grey' : 'orange'" dark>
                        <v-icon x-small left>{{ isActivity ? 'mdi-history' : 'mdi-lock' }}</v-icon>
                        {{ isActivity ? 'Attività' : 'Privato' }}
                    </v-chip>
                    {{ comment.created_at | datetime }}
                    <v-icon small class="ml-2">
                        {{ collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                    </v-icon>
                </div>
            </div>
        </v-card-title>

        <!-- Preview quando collassato -->
        <v-card-text
            v-if="collapsed"
            class="pa-3 pt-0 grey--text text-truncate cursor-pointer"
            @click="$emit('toggle')"
        >
            {{ getPreview }}
        </v-card-text>

        <!-- Contenuto completo quando espanso -->
        <template v-else>
            <v-divider></v-divider>

            <!-- Modalità Edit -->
            <v-card-text v-if="isEditing" class="pa-3">
                <editor-mention v-model="editText"></editor-mention>
            </v-card-text>

            <!-- Contenuto normale -->
            <v-card-text v-else class="pa-3">
                <div
                    v-if="isHtml(comment.text)"
                    v-html="sanitizeHtml(comment.text)"
                    class="comment-body"
                ></div>
                <div v-else class="comment-body">
                    {{ comment.text }}
                </div>
            </v-card-text>

            <!-- Azioni -->
            <v-card-actions v-if="canEdit || canDelete || isEditing" class="pa-3 pt-0">
                <v-spacer></v-spacer>

                <!-- Bottoni in modalità edit -->
                <template v-if="isEditing">
                    <v-btn text small @click="cancelEdit">Annulla</v-btn>
                    <v-btn color="success" small @click="saveEdit" :disabled="!editText">Salva</v-btn>
                </template>

                <!-- Bottoni normali -->
                <template v-else>
                    <v-btn v-if="canEdit" icon small @click="startEdit">
                        <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn v-if="canDelete" icon small color="error" @click="deleteComment">
                        <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                </template>
            </v-card-actions>
        </template>
    </v-card>
</template>

<script>
import EditorMention from '@/components/common/EditorMention'
import { sanitizeHtml } from '@/utils/sanitizeHtml'

export default {
    name: 'CommentTimelineItem',

    components: {
        EditorMention
    },

    props: {
        comment: {
            type: Object,
            required: true
        },
        collapsed: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            isEditing: false,
            editText: ''
        }
    },

    computed: {
        isActivity() {
            return this.comment.type === 'activity'
        },

        isOwner() {
            const userId = this.comment.user_id || this.comment.user?.id
            return userId === this.$store.getters.getProfile.id
        },

        canEdit() {
            return this.isOwner && this.comment.editable && !this.isActivity
        },

        canDelete() {
            return this.isOwner && this.comment.erasable && !this.isActivity
        },

        getInitial() {
            if (this.comment.user && this.comment.user.name) {
                return this.comment.user.name.charAt(0).toUpperCase()
            }
            return 'S'
        },

        getPreview() {
            if (!this.comment.text) return ''
            const text = this.comment.text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
            return text.length > 150 ? text.substring(0, 150) + '...' : text
        }
    },

    methods: {
        sanitizeHtml,

        isHtml(content) {
            if (!content) return false
            return /<[a-z][\s\S]*>/i.test(content)
        },

        startEdit() {
            this.editText = this.comment.text
            this.isEditing = true
        },

        cancelEdit() {
            this.isEditing = false
            this.editText = ''
        },

        saveEdit() {
            this.$emit('update', { id: this.comment.id, text: this.editText })
            this.isEditing = false
        },

        deleteComment() {
            this.$emit('delete', this.comment.id)
        }
    }
}
</script>

<style scoped>
.custom-avatar {
    background-size: cover;
    background-position: center;
}

.comment-body {
    max-height: 300px;
    overflow-y: auto;
}
</style>
